import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ProgressBar, Button, notify } from 'app/components';
import { Footer, Header } from './components';
import { Step1, Step2, Step3, Step4 } from './containers';
import PublishEventModal from './components/step-4/publishEventModal';
import VendorModal from './components/step-4/vendorModal';
import { createEvent, getBusinessConfig, getEvent, getPastEventList, updateEvent } from './api';
import { BellRinging } from '@phosphor-icons/react';
import './style';
import { isPast } from 'date-fns';

const EventCreate = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [subStep, setSubStep] = useState({
    step: 1,
    source: 'next',
  });
  const [progress, setProgress] = useState(6.6);
  const [isPublishModalOpen, setIsPublishModalOpen] = useState(false);
  const [isVendorModalOpen, setIsVendorModalOpen] = useState(false);
  const [acceptLoading, setAcceptLoading] = useState(false);
  const timeout = useRef(null);
  const eventImagesRef = useRef(null);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();

  const { eventData, savedEventData, businessConfig = {} } = useSelector((state) => state.eventCreate);
  const { userData, activeRequests } = useSelector((state) => state.app);

  console.log("event data", eventData)

  useEffect(() => () => {
    dispatch({ type: 'EVENT_CREATE:RESET' });
    if (timeout.current) clearTimeout(timeout.current);
  }, []);

  useEffect(() => {
    if (userData?.business_config_id) getBusinessConfig(userData.business_config_id);
  }, [userData?.business_config_id]);

  useEffect(() => {
    if (savedEventData?._id) {
      navigate(`/event/edit/${savedEventData._id}`);
      if (timeout.current) clearTimeout(timeout.current);
    }
  }, [savedEventData?._id]);

  useEffect(() => {
    if (params?.eventId && savedEventData?._id !== params.eventId) getEvent(params.eventId);
  }, [params?.eventId]);

  const updateStep = (newStep) => {
    if (subStep?.source === 'next') setSubStep({ step: 1, source: 'next' });
    else setSubStep({ step: 'final', source: 'prev' });

    setCurrentStep(newStep);
  };

  const getCurrentStep = () => {
    switch (currentStep) {
    case 1:
      return (
        <Step1
          updateStep={updateStep}
          updateProgress={setProgress}
          updateSubStep={setSubStep}
          subStep={subStep}
          eventImagesRef={eventImagesRef}
        />
      );
    case 2:
      return (
        <Step2
          updateStep={updateStep}
          updateProgress={setProgress}
          updateSubStep={setSubStep}
          saveEvent={saveEvent}
          subStep={subStep}
        />
      );
    case 3:
      return (
        <Step3
          updateStep={updateStep}
          updateProgress={setProgress}
          subStep={subStep}
        />
      );
    case 4:
      return <Step4 saveEvent={saveEvent} />;
    default:
      return null;
    }
  };

  const saveEvent = (data, callback, isPublish) => {
    if (data?.name && !activeRequests?.includes('save-event')) {
      const payload = { ...data, business_config_id: userData?.business_config_id ? Number(userData?.business_config_id) : undefined };
      if (savedEventData?._id) updateEvent(savedEventData._id, payload, isPublish, callback);
      else createEvent(payload, callback);
    }
  }; 

  useEffect(() => {
    if (timeout.current) {
      clearTimeout(timeout.current);
      timeout.current = null;
    }

    timeout.current = setTimeout(() => {
      saveEvent(eventData);
    }, 5000);
  }, [eventData]);

  const onSave = (path = null) => {
    if (eventData?.name) {
      saveEvent(eventData, () => {
        navigate(path || '/events');
      });
    } else navigate(path || '/events');
  };

  const onPublish = () => {
    setAcceptLoading(true);
    saveEvent(eventData, () => {
      setAcceptLoading(false);
      navigate('/events', { state: 'publish-success' });
    }, true);
  };

  const onClickPublish = () => {
    if (!isPast(eventData?.start_date)) {
      getPastEventList(userData?.business_config_id, (eventList) => {
        if (eventList?.length) onPublish();
        else setIsVendorModalOpen(true);
      })
    } else {
      notify('error', 'Invalid event date');
    }
  };

  const onNext = () => {
    setSubStep((prev) => {
      if (currentStep === 3 && businessConfig?.proof_data) {
        if (!savedEventData?.approved) {
          setIsPublishModalOpen(true);
        } else {
          onSave();
        }
        
        return prev;
      }

      return ({ step: prev?.step + 1, source: 'next' });
    });
  };

  const onBack = () => {
    setSubStep((prev) => {
      if (prev?.step === 1 && currentStep === 1) {
        onSave(location.state?.from);
        return;
      }

      return { step: prev?.step - 1, source: 'prev' };
    });
  };

  return (
    <div className={`event-create-wrapper mt-[100px] overflow-hidden flex-1 w-full h-full min-h-0 flex flex-col items-center${currentStep === 4 ? ' step-4' : ''}`}>
      {(currentStep !== 4 && window.innerWidth > 650) && (
        <Header
          onSave={onSave}
          isLoading={activeRequests?.includes('save-event')}
        />
      )}
      {(currentStep === 1 && subStep?.step === 1 && window.innerWidth > 650) && (
        <div className='flex py-4 px-6 bg-grey-5 rounded-xl justify-between items-center gap-[18px] w-[45%]'>
          <div className='flex gap-6 items-center'>
            <div className='bg-white flex items-center justify-center p-3 rounded-2xl'>
              <BellRinging size={32} className='text-primary' />
            </div>
            <p>Can’t find the event you’re looking for? Request it here to check for <span className='text-primary font-semibold'>Resell Ticket</span> availability!</p>
          </div>
          <Button type="primary-black" className="shrink-0" size="small" onClick={()=>navigate("/request")}>Request Event</Button>
        </div>
      )}
      <div className={`event-create-container overflow-hidden min-h-0 flex flex-col flex-1 ${currentStep === 4 ? 'full-width' : 'pt-8'}`}>
        {currentStep !== 4 && (
          <ProgressBar className="mb-12"
            steps={3} 
            value={progress}
          />
        )}
        <div className="event-create-content overflow-hidden min-h-0 flex-1 flex flex-col">
          {getCurrentStep()}
        </div>
      </div>
      {currentStep !== 4 && (
        <Footer
          onClickNext={onNext}
          onClickBack={onBack}
          isPrimaryButtonDisabled={activeRequests?.includes('send-otp')}
          showPublish={currentStep === 3 && businessConfig?.proof_data && !savedEventData?.approved}
        />
      )}
      <PublishEventModal
        isOpen={isPublishModalOpen}
        eventData={eventData}
        onClose={() => setIsPublishModalOpen(false)}
        onConfirm={onClickPublish}
      />
      <VendorModal
        isOpen={isVendorModalOpen}
        onClose={() => setIsVendorModalOpen(false)}
        onConfirm={onPublish}
        organizerName={businessConfig?.company_name}
        loading={acceptLoading}
      />
    </div>
  );
};

export default EventCreate;
